<template>
  <b-sidebar
    id="add-new-guest-sidebar"
    :visible="isAddNewGuestSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="(val) => { $emit('update:is-add-new-guest-sidebar-active', val); if (!val) $emit('sidebar-closed'); }"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ isEditMode ? 'Edit Guest' : 'Add New Guest' }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />

      </div>

      <!-- UNIFIED FORM -->
      <validation-observer
        #default="{ handleSubmit }"
        ref="refFormObserver"
      >
        <!-- Form -->
        <b-form
          class="p-2"
          autocomplete="off"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- Member Attendee Section (only show when not editing) -->
          <template v-if="!isEditMode">
            <h6>Add Member Attendee</h6>
            <p class="text-muted small mb-2">Search and select an existing user from the system</p>
            
            <b-form-group
              label="Search Users"
              label-for="user-select"
            >
              <v-select
                id="user-select"
                v-model="selectedUser"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="userOptions"
                :reduce="user => user"
                :clearable="true"
                :disabled="hasManualData"
                label="name"
                placeholder="Search for a user by name..."
              >
                <template #option="{ name, email }">
                  <div>
                    <strong>{{ name }}</strong>
                    <br>
                    <small class="text-muted">{{ email }}</small>
                  </div>
                </template>
                <span slot="no-options">
                  No users found matching your search.
                </span>
              </v-select>
              <small v-if="hasManualData" class="text-muted">
                Clear the manual fields below to select a user from the dropdown
              </small>
            </b-form-group>

            <div class="text-center my-2">
              <span class="text-muted">--- OR ---</span>
            </div>
          </template>

          <!-- Non-member Attendee Section -->
          <h6>{{ isEditMode ? 'Edit Attendee Details' : 'Add Non-member Attendee' }}</h6>
          <p class="text-muted small mb-2">
            {{ isEditMode ? 'Update the attendee details below' : 'Manually enter details for someone not in the system' }}
          </p>
          
          <!-- Name -->
          <validation-provider
            #default="validationContext"
            name="Name"
            :rules="getNameValidationRules"
          >
            <b-form-group
              label="Name"
              label-for="name"
            >
              <b-form-input
                id="name"
                v-model="guestData.name"
                :state="getFieldState(validationContext)"
                :disabled="hasSelectedUser"
                trim
                autocomplete="off"
                placeholder="Enter attendee's full name"
              />

              <b-form-invalid-feedback v-if="validationContext.validated">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Organization -->
          <validation-provider
            #default="validationContext"
            name="Organization"
            :rules="getOrgValidationRules"
          >
            <b-form-group
              label="Organization"
              label-for="organization"
            >
              <b-form-input
                id="organization"
                v-model="guestData.organization"
                :state="getFieldState(validationContext)"
                :disabled="hasSelectedUser"
                trim
                autocomplete="off"
                placeholder="Enter organization name"
              />

              <b-form-invalid-feedback v-if="validationContext.validated">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <small v-if="hasSelectedUser" class="text-muted">
            Clear the user selection above to manually enter attendee details
          </small>

          <!-- Validation Message -->
          <div v-if="showValidationError" class="alert alert-danger mt-2">
            Please either select a user from the dropdown OR enter manual attendee details, but not both.
          </div>

          <!-- Form Actions -->
          <div class="d-flex mt-3">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
              :disabled="!canSubmit"
            >
              {{ isEditMode ? 'Update Attendee' : 'Add Attendee' }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
            >
              Cancel
            </b-button>
          </div>

        </b-form>
      </validation-observer>

    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BFormInvalidFeedback, BButton,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { ref, computed, watch, nextTick } from '@vue/composition-api'
import { required } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import vSelect from 'vue-select'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddNewGuestSidebarActive',
    event: 'update:is-add-new-guest-sidebar-active',
  },
  props: {
    isAddNewGuestSidebarActive: {
      type: Boolean,
      required: true,
    },
    attendees: {
      type: Array,
      default: () => [],
    },
    allUsers: {
      type: Array,
      default: () => [],
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    editingAttendee: {
      type: Object,
      default: null,
    },
  },
  methods: {
    addGuest(guest) {
      this.$parent.$refs.guests.value.push(guest)
    },
  },
  data() {
    return {
      required,
    }
  },
  setup(props, { emit }) {
    // Use toast
    const toast = useToast()

    const blankGuestData = {
      name: '',
      organization: '',
    }

    const guestData = ref(JSON.parse(JSON.stringify(blankGuestData)))
    const selectedUser = ref(null)
    const showValidationError = ref(false)
    const isSubmitting = ref(false)

    const resetGuestData = () => {
      guestData.value = JSON.parse(JSON.stringify(blankGuestData))
      selectedUser.value = null
      showValidationError.value = false
    }

    // Computed properties for form state
    const hasSelectedUser = computed(() => selectedUser.value !== null)
    const hasManualData = computed(() => 
      guestData.value.name.trim() !== '' || guestData.value.organization.trim() !== ''
    )
    
    const canSubmit = computed(() => 
      hasSelectedUser.value || hasManualData.value
    )

    // Dynamic validation rules
    const getNameValidationRules = computed(() => {
      if (hasSelectedUser.value && !props.isEditMode) return '' // No validation needed if user is selected (and not editing)
      return 'required|min:3|max:100'
    })

    const getOrgValidationRules = computed(() => {
      if (hasSelectedUser.value && !props.isEditMode) return '' // No validation needed if user is selected (and not editing)
      return 'required|min:2|max:100'
    })

    // Track already-added member attendees so they don't appear again in the selectable list
    const existingMemberUserIds = computed(() => (props.attendees || [])
      .filter(a => a && a.is_member)
      .map(a => a.id))

    // Computed property for user options excluding existing member attendees
    const userOptions = computed(() => {
      const excludedIds = new Set(existingMemberUserIds.value)
      return props.allUsers
        .filter(user => user && !excludedIds.has(user.id))
        .map(user => ({
          ...user,
          // Ensure we have a name field for display
          name: user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User',
          email: user.email || ''
        }))
    })

    // Watch for conflicting inputs
    watch([selectedUser, guestData], () => {
      showValidationError.value = false
    }, { deep: true })

    // When switching to a selected user (making manual fields optional) clear validation errors
    watch(hasSelectedUser, (val) => {
      if (val && !props.isEditMode && refFormObserver.value) {
        // Reset all provider states to avoid stale error flash
        const providers = refFormObserver.value.refs || {}
        Object.values(providers).forEach(p => p && typeof p.reset === 'function' && p.reset())
      }
    })

    // Watch for editing attendee changes to pre-populate form
    watch(() => props.editingAttendee, (newAttendee) => {
      if (newAttendee && props.isEditMode) {
        // Pre-populate form with editing attendee data
        guestData.value = {
          name: newAttendee.name || '',
          organization: newAttendee.organisation_name || '',
        }
        selectedUser.value = null // Clear user selection in edit mode
      }
    }, { immediate: true })

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetGuestData)

    // Remove duplicated onSubmit declarations and replace with single async version
    const onSubmit = async () => {
      if (isSubmitting.value) return

      if (props.isEditMode) {
        if (!hasManualData.value) {
          showValidationError.value = true
          return
        }
        isSubmitting.value = true
        const guestToUpdate = {
          name: guestData.value.name,
          org: guestData.value.organization,
          isMember: false,
          avatar: null,
        }
        emit('guest-added', guestToUpdate)
        isSubmitting.value = false
        emit('update:is-add-new-guest-sidebar-active', false)
        return
      }

      if (hasSelectedUser.value && hasManualData.value) {
        showValidationError.value = true
        return
      }
      if (!hasSelectedUser.value && !hasManualData.value) {
        showValidationError.value = true
        return
      }

      isSubmitting.value = true
      let guestToAdd
      if (hasSelectedUser.value) {
        const user = selectedUser.value
        guestToAdd = {
          name: user.name,
          org: user.organization || user.company || '',
          isMember: true,
          avatar: user.avatar || null,
          userId: user.id,
        }
      } else {
        guestToAdd = {
          name: guestData.value.name,
          org: guestData.value.organization,
          isMember: false,
          avatar: null,
        }
      }
      emit('guest-added', guestToAdd)
      isSubmitting.value = false
      emit('update:is-add-new-guest-sidebar-active', false)
    }

    // Helper to compute field state only after validation run to prevent flash
    const getFieldState = (ctx) => {
      if (!ctx || !ctx.validated) return null
      return ctx.valid ? true : false
    }

    return {
      guestData,
      selectedUser,
      userOptions,
  existingMemberUserIds,
      showValidationError,
      hasSelectedUser,
      hasManualData,
      canSubmit,
      getNameValidationRules,
      getOrgValidationRules,
      onSubmit,
      isSubmitting,
      refFormObserver,
      getValidationState,
      resetForm,
      getFieldState,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#add-new-guest-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
